import ArchitectureDiagram from "../components/ArchitectureDiagram";
import BenefitsSection from "../components/BenefitsSection";
import DefeasibleExplainer from "../components/DefeasibleExplainer";

export default function Home() {
  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-8 md:py-16 overflow-x-hidden">
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
          Multi-Agent Plan Coordination
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-base-content/60 mb-6 md:mb-8">
          Coordinate work between many humans and LLM agents using non-monotonic logic.
        </p>

        {/* Key value props */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-base-content/70 mb-8">
          <span>Realtime</span>
          <span className="text-base-content/30">•</span>
          <span>Leaderless</span>
          <span className="text-base-content/30">•</span>
          <span>Conflict-free</span>
          <span className="text-base-content/30">•</span>
          <span>Web + CLI</span>
          <span className="text-base-content/30">•</span>
          <span>Local-first</span>
          <span className="text-base-content/30">•</span>
          <span>BYO agents</span>
        </div>

        {/* App Screenshot */}
        <div className="mockup-browser bg-base-300 border border-base-300 shadow-xl mb-8 md:mb-12">
          <div className="mockup-browser-toolbar">
            <a href="https://hence.run/p/pKuAhTepSW6F-uerLgq_rgAAAAAAaaTP6_S1Sav-KWIbVw2mDslymK9uGMr3ZlNhc2NjudVQGYP6" className="input link link-hover">https://hence.run/p/pKuAhTepSW6F...</a>
          </div>
          <div className="bg-base-200">
            <img
              src="https://imagedelivery.net/O-SJhBv1S1zUZFvTxrBOhQ/ce3471ab-0492-4e11-74e0-202d9aa89600/public"
              alt="Hence.run web app showing DAG view of multi-agent task coordination"
              className="w-full"
            />
          </div>
        </div>

        {/* Getting Started */}
        <div className="mt-8 md:mt-12 text-left">
          <h2 className="text-lg font-semibold mb-3">Getting Started</h2>
          <div className="bg-neutral text-neutral-content rounded-lg p-4 text-xs sm:text-sm font-mono overflow-x-auto">
            <div className="text-neutral-content/50"># Install CLI</div>
            <div>$ curl -fsSL hence.run/install | bash</div>
            <div className="text-neutral-content/50 mt-2"># Teach Claude (or any agent) how to use hence</div>
            <div>$ hence llm | claude</div>
          </div>
          <p className="text-sm text-base-content/50 mt-3">
            The{" "}
            <a
              href="https://codeberg.org/anuna/hence"
              className="link link-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              hence CLI
            </a>{" "}
            and{" "}
            <a
              href="https://codeberg.org/anuna/spindle-racket"
              className="link link-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Spindle reasoner
            </a>{" "}
            are free, open source software.
          </p>
          <p className="text-sm text-base-content/50 mt-2">
            Prefer to build from source? See the{" "}
            <a
              href="https://codeberg.org/anuna/hence#installation-from-source"
              className="link link-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              installation instructions
            </a>.
          </p>
        </div>

        <div className="mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 text-left">
          <div className="card bg-base-100 shadow-md">
            <div className="card-body">
              <h3 className="card-title text-lg">Append-Only Log</h3>
              <p className="text-base-content/60">
                Claims are signed and appended. No state conflicts to resolve.
                Conclusions are derived from the full set of facts and rules.
              </p>
            </div>
          </div>

          <div className="card bg-base-100 shadow-md">
            <div className="card-body">
              <h3 className="card-title text-lg">Defeasible Reasoning</h3>
              <p className="text-base-content/60">
                Builds on 50 years of AI research. Rules can be defeated by exceptions.
                Priorities resolve conflicts. Linear time complexity.
              </p>
            </div>
          </div>

          <div className="card bg-base-100 shadow-md">
            <div className="card-body">
              <h3 className="card-title text-lg">Offline-First</h3>
              <p className="text-base-content/60">
                Work offline, sync when connected. Pending claims queue locally and push on
                reconnect.
              </p>
            </div>
          </div>
        </div>

        {/* Unix Philosophy */}
        <div className="mt-10 md:mt-16 text-left">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-6">Unix Philosophy</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="card bg-base-100 border border-base-300">
              <div className="card-body p-5">
                <h3 className="font-bold text-lg">Agent Frameworks</h3>
                <p className="text-xs text-base-content/50 mb-3">$2.8B in VC funding for bad abstractions!</p>
                <ul className="space-y-2 text-sm text-base-content/70">
                  <li className="flex items-start gap-2">
                    <span className="text-base-content/40 mt-0.5">-</span>
                    <span>Chain your LLMChain to a SequentialChain</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-base-content/40 mt-0.5">-</span>
                    <span>The Witness supervises the Polecats</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-base-content/40 mt-0.5">-</span>
                    <span>Wrap your OutputParser in a PydanticOutputParser</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-base-content/40 mt-0.5">-</span>
                    <span>300+ Rust crates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-base-content/40 mt-0.5">-</span>
                    <span>More agents, more problems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-base-content/40 mt-0.5">-</span>
                    <span>Learn our SDK before we pivot</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="card bg-base-100 border border-base-300">
              <div className="card-body p-5">
                <h3 className="font-bold text-lg">Hence</h3>
                <p className="text-xs text-base-content/50 mb-3">A file format and a CLI tool</p>
                <ul className="space-y-2 text-sm text-base-content/70">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">+</span>
                    <span>Plain text files, version controlled</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">+</span>
                    <span>Works with any agent (Claude, GPT, local)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">+</span>
                    <span>Pipe to existing tools</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">+</span>
                    <span>Adopt incrementally</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">+</span>
                    <span>Plans that plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">+</span>
                    <span>Patterns that outlast the next model release</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-base-content/50 mt-4">
            Do one thing well. Compose with other tools.
          </p>
        </div>

        {/* Why agents need this */}
        <BenefitsSection />

        {/* Architecture overview */}
        <ArchitectureDiagram />

        {/* Defeasible logic explainer */}
        <DefeasibleExplainer />
      </div>
    </div>
  );
}
